import React, { lazy, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/libs/context/auth'
import { Spin } from 'antd'

import AuthLayout from '@/features/auth/components/AuthLayout'
import NoMenuLayout from '@/layouts/NoMenuLayout'
import SidebarMenu from '@/layouts/SidebarMenu'

import LoginView from '@/features/auth/views/LoginView'
import RegisterView from '@/features/auth/views/RegisterView'
import ForgotPasswordView from '@/features/auth/views/ForgotPasswordView'
import SimpleOnboardingGuard from '@/features/onboarding/SimpleOnboardingGuard'

const DashboardView = lazy(() => import('@/features/dashboard/views/DashboardView'))
const StatsView = lazy(() => import('@/features/stats/views/StatsView'))
const AccountView = lazy(() => import('@/features/account/views/AccountView'))
const OrganizationView = lazy(() => import('@/features/account/views/OrganizationView'))
const SubscriptionsView = lazy(() => import('@/features/account/views/SubscriptionsView'))
const PermissionsView = lazy(() => import('@/features/account/views/PermissionsView'))
const ResetView = lazy(() => import('@/features/auth/views/ResetView'))
const TermsView = lazy(() => import('@/features/auth/views/TermsView'))
const DeleteConfirmView = lazy(() => import('@/features/account/views/DeleteConfirmView'))
const DeleteRecoverView = lazy(() => import('@/features/account/views/DeleteRecoverView'))
const NotFoundView = lazy(() => import('@/components/NotFoundView'))
const SurveyView = lazy(() => import('@/features/survey/views/SurveyView'))
const SurveyOrganization = lazy(() => import('@/features/survey/views/OrganizationView'))
const SurveyResponse = lazy(() => import('@/features/survey/views/ResponseView'))
const SurveyAccessRequest = lazy(() => import('@/features/survey/views/AccessRequestView'))
const FingerprintDisplay = lazy(() => import('@/features/survey/views/Fingerprint'))
const ProductConfigView = lazy(() => import('@/features/product-config/views/ProductConfigView'))

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <Spin size="large" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />
  }

  return children
}

const AppRoutes = () => {
  const location = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  return (
    <Routes>
      <Route path="/auth" element={<AuthLayout />}>
        <Route path="login" element={<LoginView />} />
        <Route path="register" element={<RegisterView />} />
        <Route path="forgot-password" element={<ForgotPasswordView />} />
        <Route index element={<Navigate to="/auth/login" replace />} />
      </Route>
      <Route element={<AuthLayout />}>
        <Route path="/reset-password/:token" element={<ResetView />} />
        <Route path="/account-activation/:token" element={<TermsView />} />
        <Route path="/account-deletion/confirm/:token" element={<DeleteConfirmView />} />
        <Route path="/account-deletion/recover/:token" element={<DeleteRecoverView />} />
      </Route>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <SimpleOnboardingGuard />
            <NoMenuLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardView />} />
        <Route path="dashboard" element={<DashboardView />} />
        <Route path="user" element={<AccountView />} />
        <Route path="organization" element={<OrganizationView />} />
        <Route path="subscriptions" element={<SubscriptionsView />} />
        <Route path="permissions" element={<PermissionsView />} />
        <Route path="product-config" element={<ProductConfigView />} />

        {/* Survey routes */}
        <Route path="survey">
          <Route index element={<SurveyView />} />
          <Route path="organization" element={<SurveyOrganization />} />
        </Route>
      </Route>
      <Route path="survey/response/:token" element={<SurveyResponse />} />
      <Route path="request-survey-access/:token" element={<SurveyAccessRequest />} />
      <Route path="fingerprint" element={<FingerprintDisplay />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <SidebarMenu />
          </ProtectedRoute>
        }
      >
        <Route path="stats" element={<StatsView />} />
      </Route>
      ,{/* Redirect root to dashboard or login */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />,{/* 404 page */}
      <Route path="*" element={<NotFoundView />} />
    </Routes>
  )
}

export default AppRoutes
